import { GoogleMap } from '@react-google-maps/api'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { connect, ConnectedProps } from 'react-redux'
import styled from 'styled-components'

import { MapRoute, MapMarker } from './MapElement'
import ExploreCardCarousel from './MapElement/ExploreCardCarousel'
import ExploreCardSingle from './MapElement/ExploreCardSingle'
import ExploreMapMarker from './MapElement/ExploreMapMarker'
import {
  setMapMarkers,
  isRouteItem,
  isPinnedItem,
  mapToMarker,
  mapToRoute,
  mapToExploreMarker,
  isMapItem,
  ExploreMapMarkerProps,
  panToIntoViewIfObstructed,
} from './utils'
import TooltipModal from '../TripModal/TooltipModal'

import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { useGoogleMapsContext } from 'contexts/GoogleMapsContext'
import { useAppDispatch } from 'hooks/reduxHooks'
import useHeldValue from 'hooks/useHeldValue'
import usePrevious from 'hooks/usePrevious'
import { useResponsive } from 'hooks/useResponsive'
import { EmptyArray } from 'lib/array/arrayUtils'
import {
  DEFAULT_MAP_GEO,
  DEFAULT_MAP_OPTIONS,
  DEFAULT_MAP_ZOOM,
  MOBILE_MAP_OPTIONS,
} from 'tripPlanner/config/map'
import ExploreQueryContext from 'tripPlanner/contexts/Explore/ExploreQueryContext'
import { useTrip } from 'tripPlanner/hooks/api'
import { useExploreItems } from 'tripPlanner/hooks/api/explore'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import {
  clearCurrentSectionId,
  setCurrentSectionId,
} from 'tripPlanner/reducers/actions'
import { ExploreItem } from 'tripPlanner/types/common'
import { PublicMapItem } from 'tripPlanner/types/publicTrip'
import { MapItem, MarkerItem, RouteItem } from 'tripPlanner/types/tripItem'
import { assertUnreachable } from 'tripPlanner/utils'
import {
  isExploreItem,
  filterSavedItemsWithExplorePlaces,
  getPlaceIdToSavedExploreItemMap,
  isExploreMarker,
} from 'tripPlanner/utils/explore'
import { itemSectionId } from 'tripPlanner/utils/itemSection'
import {
  getSelectedItineraryDate,
  getTripFilters,
} from 'tripPlanner/utils/selectors'
import { filterSummaryMapItems } from 'tripPlanner/utils/summary'
import { TripTab } from 'tripPlanner/utils/tabs'

const containerStyle = {
  height: '100%',
  minHeight: '80vh',
}

const ModalContainer = styled.div`
  ${mediaQueryUp.tablet} {
    display: none;
  }
`

const DesktopRoot = styled.div`
  height: 100%;
`

interface MapProps {
  items: (MapItem | PublicMapItem)[]
  title: string
  tab: TripTab
  tripId: string
  canEdit: boolean
  mapOptions?: google.maps.MapOptions
  mapContainerClassName?: string
  currentSectionId?: string
  /**
   * Currently only affects explore items.
   * - If true, the map will use a carousel to display explore item cards.
   * - If false, selecting an explore item will bring up a single card.
   */
  useCardCarousel: boolean
}

// Returns routable & single place items
function splitItems(
  items: (MapItem | PublicMapItem)[],
): [RouteItem[], MarkerItem[]] {
  const pinned: MarkerItem[] = []
  const routed: RouteItem[] = []

  for (const item of items) {
    if (isRouteItem(item)) {
      routed.push(item)
    }

    if (isPinnedItem(item)) {
      pinned.push(item)
    }
  }

  return [routed, pinned]
}

function BaseTripMap({
  items,
  tripId,
  canEdit,
  tab = 'Summary',
  mapOptions = DEFAULT_MAP_OPTIONS,
  mapContainerClassName,
  filters = EmptyArray,
  currentSectionId,
  useCardCarousel,
}: ConnectedProps<typeof connector>) {
  const dispatch = useAppDispatch()
  const onSelectMapItem = useCallback(
    (key: string | undefined) => {
      if (key === undefined) {
        dispatch(clearCurrentSectionId())
      } else {
        dispatch(setCurrentSectionId(key))
      }
    },
    [dispatch],
  )
  const onSelectExploreItem = useCallback(
    (item: ExploreItem) => {
      dispatch(setCurrentSectionId(itemSectionId(item.id)))
    },
    [dispatch],
  )
  const onDeselectMapItem = useCallback(() => {
    dispatch(clearCurrentSectionId())
  }, [dispatch])

  const [isZooming, setIsZooming] = useState<boolean>(false) // nothing uses this state, but without it, flight paths don't render.
  const mapRef = useRef<google.maps.Map>()

  const { isLoaded } = useGoogleMapsContext()

  const {
    query: exploreQuery,
    locationBias,
    category: initialCategory,
    place: selectedPlace,
  } = useContext(ExploreQueryContext)

  const {
    isLoading: exploreIsLoading,
    data: initialExploreItems = EmptyArray as ExploreItem[],
  } = useExploreItems({
    query: exploreQuery,
    locationBias,
  })

  const exploreCategory = useHeldValue(initialCategory, exploreIsLoading)

  const exploreItems = useHeldValue(initialExploreItems, exploreIsLoading)

  const prevExploreItems = usePrevious(exploreItems)

  const prevTab = usePrevious(tab)

  const [routes, markers] = useMemo(() => {
    let itemsToUse: (MapItem | PublicMapItem)[]

    switch (tab) {
      case 'Itinerary':
        // FOR FUTURE USE:
        // It might be the case where we want to view items specific to a day:
        /*  (This would be outside the useMemo)
        const itemsOnToday = useMemo(
          // @ts-ignore
          () => generateFilteredTripItems(items, moment(selectedDate)),
          [selectedDate, items],
        ) 
        */

        // const uniqItems = itemsOnToday.map((i) => i.tripItem)
        // itemsToUse = uniqueBy(uniqItems, (i) => i.id)

        // we have to do uniqueBy in the above case since itemsToday contains
        // two versions of the same item when it has a start and end.

        itemsToUse = items.filter((tripItem) => tripItem.isInItinerary)

        break
      case 'Summary':
        itemsToUse = filterSummaryMapItems(items, filters)
        break
      case 'Explore':
        const [routeItems, pinnedItems] = splitItems(
          filterSummaryMapItems(items, filters),
        )

        return [
          routeItems.map((routeItem) => mapToRoute(routeItem, false)),
          [
            ...exploreItems.map(mapToExploreMarker),
            ...filterSavedItemsWithExplorePlaces(
              pinnedItems.flatMap((pinnedItem) =>
                mapToMarker(pinnedItem, false),
              ),
              exploreItems,
            ),
          ],
        ]

      default:
        assertUnreachable(tab)
    }

    const [routeItems, pinnedItems] = splitItems(itemsToUse)

    return [
      routeItems.map((routeItem) => mapToRoute(routeItem)),
      pinnedItems.flatMap((pinnedItem) => mapToMarker(pinnedItem)),
    ]
  }, [tab, items, filters, exploreItems])

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map
      const mapMarkers = [...markers, ...routes]
      if (mapMarkers.length > 0 || tab === 'Explore') {
        setMapMarkers([...markers, ...routes], mapRef.current, selectedPlace)
      }
      if (routes.length === 0 && markers.length === 0) {
        if (!currentSectionId) {
          map.setCenter(DEFAULT_MAP_GEO)
          map.setZoom(DEFAULT_MAP_ZOOM)
        }
      }
    },
    [routes, markers, selectedPlace, tab, currentSectionId],
  )

  useEffect(() => {
    if (!mapRef.current) {
      return
    }

    switch (tab) {
      case 'Itinerary':
      case 'Summary':
        const mapMarkers = [...markers, ...routes]
        if (mapMarkers.length > 0) {
          setMapMarkers(mapMarkers, mapRef.current, selectedPlace)
        } else {
          mapRef.current.setCenter(DEFAULT_MAP_GEO)
          mapRef.current.setZoom(DEFAULT_MAP_ZOOM)
        }
        break
      case 'Explore':
        // Don't update markers if explore is loading... its disorientating
        if (exploreIsLoading) {
          return
        }

        if (!selectedPlace) {
          mapRef.current.setCenter(DEFAULT_MAP_GEO)
          mapRef.current.setZoom(DEFAULT_MAP_ZOOM)
          return
        }

        if (prevTab !== tab || exploreItems !== prevExploreItems) {
          setMapMarkers([...markers, ...routes], mapRef.current, selectedPlace)
        } else if (
          // Case where we switch TO a place with no items...
          // FROM a place with no items. (Also triggered on load of place with no items)
          // we still need to also update the place in this case.
          exploreItems === prevExploreItems &&
          exploreItems.length === 0
        ) {
          setMapMarkers(EmptyArray, mapRef.current, selectedPlace)
        }
    }
  }, [
    routes,
    markers,
    tab,
    prevTab,
    exploreIsLoading,
    prevExploreItems,
    exploreItems,
    selectedPlace,
  ])

  const exploreMarkersBySectionId = useMemo(() => {
    const markersBySectionId = new Map<string, ExploreMapMarkerProps>()

    markers.forEach((marker) => {
      if (isExploreMarker(marker)) {
        markersBySectionId.set(itemSectionId(marker.item.id), marker)
      }
    })

    return markersBySectionId
  }, [markers])

  const activeExploreMarker = exploreMarkersBySectionId.get(
    currentSectionId as string,
  )

  const savedExploreItemsByPlace = getPlaceIdToSavedExploreItemMap(items)

  const carouselContainerRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (activeExploreMarker && mapRef.current) {
      panToIntoViewIfObstructed(
        mapRef.current,
        activeExploreMarker,
        carouselContainerRef.current,
      )
    }
  }, [activeExploreMarker])

  return (
    <>
      {isLoaded && (
        <GoogleMap
          options={mapOptions}
          onLoad={onLoad}
          onIdle={() => setIsZooming(false)}
          onZoomChanged={() => setIsZooming(true)}
          mapContainerStyle={containerStyle}
          mapContainerClassName={mapContainerClassName}
        >
          {routes.map((marker, i) => {
            const itemId = itemSectionId(marker.item.id)
            return (
              <MapRoute
                zIndex={i}
                key={`route-${i}`}
                item={marker.item}
                position={marker.position}
                active={itemId === currentSectionId}
                onSelect={() => onSelectMapItem(itemId)}
                onDeselect={() => onSelectMapItem(undefined)}
                map={mapRef.current}
                isZooming={isZooming}
              />
            )
          })}
          {markers.map((marker, i) => {
            const itemId = itemSectionId(marker.item.id)
            if (isExploreItem(marker.item)) {
              return (
                <ExploreMapMarker
                  zIndex={routes.length + i}
                  key={`marker-${marker.item.id}`}
                  category={exploreCategory}
                  item={marker.item}
                  position={marker.position}
                  active={itemId === currentSectionId}
                  saved={savedExploreItemsByPlace.has(marker.item.place.id!)}
                  onSelect={() => onSelectMapItem(itemId)}
                  onDeselect={() => onSelectMapItem(undefined)}
                  tab={tab}
                />
              )
            }
            return (
              <MapMarker
                zIndex={routes.length + i}
                key={`marker-${marker.item.id}-${marker.itemPlaceType}`}
                item={marker.item}
                position={marker.position}
                active={itemId === currentSectionId}
                onSelect={() => onSelectMapItem(itemId)}
                onDeselect={() => onSelectMapItem(undefined)}
                tab={tab}
              />
            )
          })}
          {!useCardCarousel && (
            <ExploreCardSingle
              selectedMarker={activeExploreMarker}
              onDeselect={onDeselectMapItem}
              containerRef={carouselContainerRef}
            />
          )}
          {useCardCarousel && (
            <ExploreCardCarousel
              exploreItems={exploreItems}
              selectedMarker={activeExploreMarker}
              onSelect={onSelectExploreItem}
              onDeselect={onDeselectMapItem}
              containerRef={carouselContainerRef}
            />
          )}
        </GoogleMap>
      )}
      {!isLoaded && <LoadingIndicator floating />}
      {isLoaded && items.length === 0 && canEdit && (
        <ModalContainer>
          <TooltipModal tripId={tripId} />
        </ModalContainer>
      )}
    </>
  )
}

const mapStateToProps = (state: App.State, ownProps: MapProps) => ({
  selectedDate: getSelectedItineraryDate(state),
  filters: getTripFilters(state),
  currentSectionId: state.tripPlanner.currentSectionId,
  ...ownProps,
})

const connector = connect(mapStateToProps)
export const TripMap = connector(BaseTripMap)

export function DesktopTripMap(props: MapProps) {
  return (
    <DesktopRoot>
      <TripMap {...props} />
    </DesktopRoot>
  )
}

// We omit items here and instead get it in the actual component itself since this is
// used in a modal. Modals can only handle static data. Not dynamic data such as items
type MobileMapProps = Omit<MapProps, 'items'>

export function MobileTripMap(props: MobileMapProps) {
  const { closeModal, goBack } = useModalCallbacks()

  const [match] = useResponsive()

  useEffect(() => {
    if (match.desktop || match.largeDesktop) {
      closeModal()
    }
  }, [match, closeModal])

  const { data: trip } = useTrip({ tripId: props.tripId })
  const { items = EmptyArray } = trip ?? {}
  const mapItems = useMemo(() => items.filter(isMapItem), [items])

  return (
    <>
      <ModalHeader
        title={props.title}
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <TripMap {...props} items={mapItems} mapOptions={MOBILE_MAP_OPTIONS} />
    </>
  )
}
